import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "react-router-dom";
import { Plus, Search, Pencil, Trash2, Eye, Send } from "lucide-react";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import toast from "react-hot-toast";
import {
  Button,
  Input,
  Select,
  Table,
  Pagination,
  Card,
  ConfirmModal,
  StatusBadge,
} from "@/components/ui";
import { budgetsApi } from "@/services/api";
import { BudgetStatus } from "@shared/types";

type StatusFilter = BudgetStatus | "all";

const STATUS_OPTIONS: { value: StatusFilter; label: string }[] = [
  { value: "all", label: "Todos os status" },
  { value: "draft", label: "Rascunho" },
  { value: "sent", label: "Enviado" },
  { value: "viewed", label: "Visualizado" },
  { value: "approved", label: "Aprovado" },
  { value: "rejected", label: "Rejeitado" },
  { value: "signed", label: "Assinado" },
  { value: "expired", label: "Expirado" },
  { value: "cancelled", label: "Cancelado" },
];

export default function BudgetsPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [search, setSearch] = useState("");
  const [status, setStatus] = useState<StatusFilter>("all");
  const [page, setPage] = useState(1);
  const [deleteId, setDeleteId] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ["budgets", { search, status, page }],
    queryFn: () =>
      budgetsApi.list({
        search,
        status: status === "all" ? undefined : status,
        page,
        per_page: 10,
      }),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => budgetsApi.delete(id),
    onSuccess: () => {
      toast.success("Orçamento removido com sucesso");
      queryClient.invalidateQueries({ queryKey: ["budgets"] });
      setDeleteId(null);
    },
    onError: () => {
      toast.error("Erro ao remover orçamento");
    },
  });

  const sendMutation = useMutation({
    mutationFn: (id: string) => budgetsApi.send(id),
    onSuccess: () => {
      toast.success("Orçamento enviado com sucesso");
      queryClient.invalidateQueries({ queryKey: ["budgets"] });
    },
    onError: () => {
      toast.error("Erro ao enviar orçamento");
    },
  });

  const budgets = data?.data?.items || [];
  const total = data?.data?.total || 0;
  const totalPages = Math.ceil(total / 10);

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(value);
  };

  const handleSend = (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    sendMutation.mutate(id);
  };

  const columns = [
    {
      key: "number",
      header: "Número",
      render: (budget: any) => (
        <div>
          <p className="font-mono font-medium text-primary">{budget.number}</p>
          <p className="text-xs text-text-muted">
            {format(new Date(budget.created_at), "dd 'de' MMM", {
              locale: ptBR,
            })}
          </p>
        </div>
      ),
    },
    {
      key: "client",
      header: "Cliente",
      render: (budget: any) => (
        <div>
          <p className="font-medium">
            {budget.client?.name || "Cliente não informado"}
          </p>
          {budget.client?.email && (
            <p className="text-sm text-text-muted">{budget.client.email}</p>
          )}
        </div>
      ),
    },
    {
      key: "title",
      header: "Título",
      render: (budget: any) => (
        <p className="text-text-secondary truncate max-w-xs">
          {budget.title || "-"}
        </p>
      ),
    },
    {
      key: "total",
      header: "Valor Total",
      render: (budget: any) => (
        <span className="font-mono font-medium">
          {formatCurrency(budget.total || 0)}
        </span>
      ),
    },
    {
      key: "status",
      header: "Status",
      render: (budget: any) => <StatusBadge status={budget.status} />,
    },
    {
      key: "validity_date",
      header: "Validade",
      render: (budget: any) => {
        if (!budget.validity_date)
          return <span className="text-text-muted">-</span>;
        const date = new Date(budget.validity_date);
        const isExpired = date < new Date();
        return (
          <span className={isExpired ? "text-red-400" : "text-text-secondary"}>
            {format(date, "dd/MM/yyyy")}
          </span>
        );
      },
    },
    {
      key: "actions",
      header: "",
      className: "w-32",
      render: (budget: any) => (
        <div className="flex items-center gap-1">
          <button
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/admin/budgets/${budget.id}`);
            }}
            className="p-2 text-text-muted hover:text-text-primary hover:bg-white/5 rounded-lg transition-colors"
            title="Visualizar"
          >
            <Eye className="w-4 h-4" />
          </button>
          {budget.status === "draft" && (
            <>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(`/admin/budgets/${budget.id}/edit`);
                }}
                className="p-2 text-text-muted hover:text-text-primary hover:bg-white/5 rounded-lg transition-colors"
                title="Editar"
              >
                <Pencil className="w-4 h-4" />
              </button>
              <button
                onClick={(e) => handleSend(e, budget.id)}
                className="p-2 text-text-muted hover:text-primary hover:bg-primary/10 rounded-lg transition-colors"
                title="Enviar"
                disabled={sendMutation.isPending}
              >
                <Send className="w-4 h-4" />
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setDeleteId(budget.id);
                }}
                className="p-2 text-text-muted hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"
                title="Remover"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </>
          )}
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-4 sm:space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-text-primary">
            Orçamentos
          </h1>
          <p className="text-text-secondary mt-1 text-sm sm:text-base">
            Gerencie seus orçamentos e propostas
          </p>
        </div>
        <Link to="/admin/budgets/new" className="w-full sm:w-auto">
          <Button
            leftIcon={<Plus className="w-5 h-5" />}
            className="w-full sm:w-auto"
          >
            Novo Orçamento
          </Button>
        </Link>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
        <StatCard
          label="Rascunhos"
          value={budgets.filter((b: any) => b.status === "draft").length}
          color="text-gray-400"
        />
        <StatCard
          label="Enviados"
          value={
            budgets.filter(
              (b: any) => b.status === "sent" || b.status === "viewed",
            ).length
          }
          color="text-blue-400"
        />
        <StatCard
          label="Aprovados"
          value={
            budgets.filter(
              (b: any) => b.status === "approved" || b.status === "signed",
            ).length
          }
          color="text-green-400"
        />
        <StatCard
          label="Rejeitados"
          value={budgets.filter((b: any) => b.status === "rejected").length}
          color="text-red-400"
        />
      </div>

      {/* Filters */}
      <Card className="p-4">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <Input
              placeholder="Buscar por número, cliente ou título..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              leftIcon={<Search className="w-5 h-5" />}
            />
          </div>
          <div className="sm:w-48">
            <Select
              value={status}
              onChange={(e) => {
                setStatus(e.target.value as StatusFilter);
                setPage(1);
              }}
              options={STATUS_OPTIONS}
            />
          </div>
        </div>
      </Card>

      {/* Table */}
      <Table
        columns={columns}
        data={budgets}
        keyExtractor={(budget) => budget.id}
        isLoading={isLoading}
        emptyMessage="Nenhum orçamento encontrado"
        onRowClick={(budget) => navigate(`/admin/budgets/${budget.id}`)}
      />

      {/* Pagination */}
      <Pagination
        currentPage={page}
        totalPages={totalPages}
        onPageChange={setPage}
        totalItems={total}
        itemsPerPage={10}
      />

      {/* Delete Confirmation */}
      <ConfirmModal
        isOpen={!!deleteId}
        onClose={() => setDeleteId(null)}
        onConfirm={() => deleteId && deleteMutation.mutate(deleteId)}
        title="Remover Orçamento"
        message="Tem certeza que deseja remover este orçamento? Esta ação não pode ser desfeita."
        confirmText="Remover"
        variant="danger"
        isLoading={deleteMutation.isPending}
      />
    </div>
  );
}

function StatCard({
  label,
  value,
  color,
}: {
  label: string;
  value: number;
  color: string;
}) {
  return (
    <Card className="p-4 text-center">
      <p className={`text-2xl font-bold ${color}`}>{value}</p>
      <p className="text-sm text-text-muted">{label}</p>
    </Card>
  );
}
